import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { CensusActionLabelsService } from './census-action-labels.service';
import {
  CensusActionLabel,
  CensusCodeAssignment,
  CensusCodesByActionType,
  PCC_ACTION_TYPES,
  PccActionType,
} from './census-action-labels.models';

@Component({
  selector: 'app-census-action-labels',
  templateUrl: './census-action-labels.component.html',
  styleUrls: ['./census-action-labels.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CensusActionLabelsComponent implements OnInit, OnDestroy {
  readonly actionTypeOptions = PCC_ACTION_TYPES.map(t => ({ value: t, label: t }));

  labels: CensusActionLabel[] = [];
  sortedLabels: CensusActionLabel[] = [];
  codesByType: CensusCodesByActionType = {};

  loading = false;

  // Top form (create-label)
  newLabelTypes: PccActionType[] = [];
  newLabelName = '';

  // Inline rename state — only one label edited at a time
  editingLabelId: string | null = null;
  editingLabelName = '';

  @ViewChild('renameInput') renameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('confirmDeleteDialog') confirmDeleteDialog?: TemplateRef<any>;

  // Which label's "+ Add code" dropdown is currently open
  openAddDropdownLabelId: string | null = null;

  // Context passed into the confirm-delete dialog template.
  pendingDelete: { label: CensusActionLabel } | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private service: CensusActionLabelsService,
    private toastr: NbToastrService,
    private cdr: ChangeDetectorRef,
    private dialogService: NbDialogService,
    private router: Router,
  ) {}

  goToAdtReport(): void {
    this.router.navigate(['/manageb/adt-report']);
  }

  ngOnInit(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Data ---

  private refresh(): void {
    this.loading = true;
    this.cdr.markForCheck();
    this.service.loadAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ labels, codes }) => {
          this.labels = labels;
          this.codesByType = codes;
          this.sortedLabels = this.sortLabels(labels);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.toastr.danger('Failed to load labels', 'Error');
          this.cdr.markForCheck();
        },
      });
  }

  // --- Reorder (Move up/down) ---

  // Flat order across all labels: any label can swap with any neighbor.
  canMoveUp(label: CensusActionLabel): boolean {
    return this.sortedLabels.findIndex(l => l._id === label._id) > 0;
  }

  canMoveDown(label: CensusActionLabel): boolean {
    const i = this.sortedLabels.findIndex(l => l._id === label._id);
    return i >= 0 && i < this.sortedLabels.length - 1;
  }

  moveLabel(label: CensusActionLabel, direction: 'up' | 'down'): void {
    const i = this.sortedLabels.findIndex(l => l._id === label._id);
    if (i < 0) return;
    const j = direction === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= this.sortedLabels.length) return;

    // Optimistic swap + renumber; snapshots let us revert on server rejection.
    const snapshotSorted = [...this.sortedLabels];
    const snapshotLabels = [...this.labels];
    const reordered = [...this.sortedLabels];
    [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
    const renumbered = reordered.map((l, idx) => ({ ...l, displayOrder: idx }));
    this.sortedLabels = renumbered;
    // Mirror the new displayOrder onto `labels` so later re-sorts match the UI.
    const byId = new Map(renumbered.map(l => [l._id, l] as const));
    this.labels = this.labels.map(l => byId.get(l._id) ?? l);
    this.cdr.markForCheck();

    this.service.moveLabel(label._id, direction)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: err => {
          this.sortedLabels = snapshotSorted;
          this.labels = snapshotLabels;
          this.cdr.markForCheck();
          this.toastr.danger(this.safeErrorMessage(err, "Couldn't reorder label (E-LABEL-MOVE)"), 'Error');
        },
      });
  }

  // Sort purely by (displayOrder, name); the Move column drives displayOrder.
  // Reports still group by action types on their own.
  private sortLabels(labels: CensusActionLabel[]): CensusActionLabel[] {
    return [...labels].sort((a, b) => {
      const ao = a.displayOrder ?? 0;
      const bo = b.displayOrder ?? 0;
      if (ao !== bo) return ao - bo;
      return a.name.localeCompare(b.name);
    });
  }

  // --- Code <-> Label accessors ---

  codesForLabel(label: CensusActionLabel): CensusCodeAssignment[] {
    return this.codesOfLabelTypes(label).filter(c => c.labelIds.includes(label._id));
  }

  codesNotInLabel(label: CensusActionLabel): CensusCodeAssignment[] {
    return this.codesOfLabelTypes(label).filter(c => !c.labelIds.includes(label._id));
  }

  private codesOfLabelTypes(label: CensusActionLabel): CensusCodeAssignment[] {
    return label.actionTypes.reduce<CensusCodeAssignment[]>((acc, t) => acc.concat(this.codesByType[t] ?? []), []);
  }

  // Names of every label currently assigned to a code, for the dropdown hint
  // ("on: Readmission, Hospital"). Shows where the code is so admins can see
  // it's already routed elsewhere before adding it to another label.
  extraLabelNames(code: CensusCodeAssignment): string[] {
    return code.labelNames.filter(n => !!n);
  }

  // --- Top form: create label ---

  // Kept in PCC order so the summary reads the same as the saved label.
  setNewLabelTypes(types: string[]): void {
    this.newLabelTypes = PCC_ACTION_TYPES.filter(t => types.includes(t));
  }

  canCreateLabel(): boolean {
    return this.newLabelTypes.length > 0 && this.newLabelName.trim().length > 0;
  }

  createLabel(): void {
    if (!this.canCreateLabel()) return;
    const name = this.newLabelName.trim();
    this.service.createLabel({ actionTypes: this.newLabelTypes, name })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.newLabelName = '';
          this.toastr.success(`Label "${name}" created`, 'Success');
          this.refresh();
        },
        error: err => {
          this.toastr.danger(this.safeErrorMessage(err, "Couldn't create label (E-LABEL-CREATE)"), 'Error');
        },
      });
  }

  // --- Label rename / delete ---

  startEdit(label: CensusActionLabel): void {
    this.editingLabelId = label._id;
    this.editingLabelName = label.name;
    this.cdr.markForCheck();
    // setTimeout defers the focus until after Angular renders the input;
    // autofocus only fires on initial page load, not on dynamic insertion.
    setTimeout(() => this.renameInput?.nativeElement.focus(), 0);
  }

  cancelEdit(): void {
    this.editingLabelId = null;
    this.editingLabelName = '';
    this.cdr.markForCheck();
  }

  saveEdit(label: CensusActionLabel): void {
    const name = this.editingLabelName.trim();
    if (!name || name === label.name) {
      this.cancelEdit();
      return;
    }
    this.service.updateLabel(label._id, { name })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.editingLabelId = null;
          this.toastr.success('Label renamed', 'Success');
          this.refresh();
        },
        error: err => {
          this.toastr.danger(this.safeErrorMessage(err, "Couldn't rename label (E-LABEL-RENAME)"), 'Error');
        },
      });
  }

  deleteLabel(label: CensusActionLabel): void {
    if (!this.confirmDeleteDialog) {
      this.performDelete(label);
      return;
    }
    this.pendingDelete = { label };
    this.dialogService
      .open(this.confirmDeleteDialog, { context: { label } as any, hasBackdrop: true, closeOnEsc: true })
      .onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmed => {
        this.pendingDelete = null;
        if (confirmed === true) this.performDelete(label);
      });
  }

  private performDelete(label: CensusActionLabel): void {
    this.service.deleteLabel(label._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success(`Label "${label.name}" deleted`, 'Success');
          this.refresh();
        },
        error: err => {
          this.toastr.danger(this.safeErrorMessage(err, "Couldn't delete label (E-LABEL-DELETE)"), 'Error');
        },
      });
  }

  // --- Code assignment ---

  toggleAddDropdown(label: CensusActionLabel): void {
    this.openAddDropdownLabelId =
      this.openAddDropdownLabelId === label._id ? null : label._id;
    this.cdr.markForCheck();
  }

  closeAddDropdown(): void {
    this.openAddDropdownLabelId = null;
    this.cdr.markForCheck();
  }

  // Add a label to the code's existing set (multi-label). Merges the label id
  // into labelIds; the server validates membership and re-resolves names on next
  // refresh.
  addCodeToLabel(code: CensusCodeAssignment, label: CensusActionLabel): void {
    if (code.labelIds.includes(label._id)) return;
    const next = [...code.labelIds, label._id];
    const nextNames = [...code.labelNames, label.name];
    this.persistLabels(code, next, nextNames);
  }

  // Remove a label from the code's set. If this was the only label, the code
  // is left unassigned and its events stop appearing in the report.
  removeCodeFromLabel(code: CensusCodeAssignment, label: CensusActionLabel): void {
    const idx = code.labelIds.indexOf(label._id);
    if (idx < 0) return;
    const next = code.labelIds.filter((_, i) => i !== idx);
    const nextNames = code.labelNames.filter((_, i) => i !== idx);
    this.persistLabels(code, next, nextNames);
  }

  private persistLabels(
    code: CensusCodeAssignment,
    nextLabelIds: string[],
    nextLabelNames: string[],
  ): void {
    const actionType = code.pccActionType ?? '';
    const previousIds = [...code.labelIds];
    const previousNames = [...code.labelNames];
    // Optimistic update — revert in the error handler if the write fails.
    this.codesByType = this.replaceCode(code.shortDesc, actionType, {
      labelIds: nextLabelIds,
      labelNames: nextLabelNames,
    });
    this.cdr.markForCheck();
    this.service.setLabels(code.shortDesc, nextLabelIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success(`Updated ${code.shortDesc}`, 'Saved');
        },
        error: err => {
          this.codesByType = this.replaceCode(code.shortDesc, actionType, {
            labelIds: previousIds,
            labelNames: previousNames,
          });
          this.cdr.markForCheck();
          this.toastr.danger(this.safeErrorMessage(err, "Couldn't assign label (E-LABEL-ASSIGN)"), 'Error');
        },
      });
  }

  // Returns a new codesByType where the (actionType, shortDesc) entry is a
  // fresh object with `patch` applied; everything else is reference-equal.
  private replaceCode(
    shortDesc: string,
    actionType: string,
    patch: Partial<CensusCodeAssignment>,
  ): CensusCodesByActionType {
    const list = this.codesByType[actionType];
    if (!list) return this.codesByType;
    const next = list.map(c => (c.shortDesc === shortDesc ? { ...c, ...patch } : c));
    return { ...this.codesByType, [actionType]: next };
  }

  // Returns server's error string only if it's a short, plain message; otherwise
  // returns `fallback`. Server now emits `{error: "<msg>"}` (not `{message}`).
  // Whitelists out Mongo dup-key payloads and structured/raw error dumps.
  private safeErrorMessage(err: any, fallback: string): string {
    const raw = err?.error?.error;
    if (typeof raw !== 'string') return fallback;
    if (raw.length === 0 || raw.length > 200) return fallback;
    if (raw.includes('E11000') || raw.includes('BulkWriteError') || raw.includes('{')) return fallback;
    return raw;
  }

  // --- TrackBy ---

  trackByLabelId(_i: number, label: CensusActionLabel): string {
    return label._id;
  }

  trackByCode(_i: number, code: CensusCodeAssignment): string {
    return code.shortDesc;
  }
}
